import { useStore } from '../../store/store';
import { App, Button, ConfigProvider, Input, Modal, Popconfirm, Radio, Table, Tabs, Tag } from 'antd';
import Tooltip from '../common/Tooltip';
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  FileOutlined,
  PlusCircleOutlined,
  StarFilled,
  StarOutlined,
} from '@ant-design/icons';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { canUseWebEditor, emptyUrl, updateUrl } from '../../utils/browser';
import { useTranslation } from 'react-i18next';
import DocumentsFolderView from '../RightSide/DocumentsFolderView';
import {
  FAVORITES_DISABLED,
  FAVORITES_DISABLED_MESSAGE,
  createProposal,
  deleteProposal,
  duplicateProposal,
  fetchFavoriteProposals,
  fetchFavoritesEnabled,
  fetchFavoriteReloadDefaults,
  renameProposal,
  setProposalFavorite,
} from './requests';
import { fetchProposal } from '../../requests/proposals';
import { useModalOpener, useQueryDocumentTypes } from '../../hooks';
import { textToRaw, triggersToRaw, } from '../../utils/dataSync';
import { mutateDefaultBoxValue } from '../../utils/boxes';
import { dedupeSectionStyles } from '../../utils/styles';
import { getProposalSections } from '../../requests/sections';
import { runReloadLinkedSections } from '../common/reloadLinkedSections';
import {
  CONSEQUENCE_STYLE,
  LinkedSectionAnswersChoice,
  QUESTION_BLOCK_STYLE,
  QUESTION_STYLE,
  RELOAD_TITLE_STYLE,
  answerOption,
} from '../common/LinkedSectionAnswersChoice';
import WebTemplatesPanel from '../RightSide/WebTemplatesPanel';
import {
  createStandaloneWebsite,
  fetchStandaloneTemplates,
} from '../../webviewer/requests/standaloneWebsite';
import { webEditorPath } from '../../webviewer/utils/webRoutes';
import {
  hasWebsite, isDocumentWebsite, isStandaloneWebsite, kindLabel, websiteKind,
} from '../../webviewer/utils/webTemplateKind';

/* Deux onglets : les documents classiques, et tous les sites web — site web
   seul ou version web d'un document, qui figure donc dans les deux
   (cf. docs/webviewer/chantier-templates-autonomes.md). */
const DOCUMENT_KIND = 'document';
const WEBSITE_KIND = 'website';
const KIND_TABS = [
  { key: DOCUMENT_KIND, label: 'Document' },
  { key: WEBSITE_KIND, label: 'Site web' },
];

// Source d'un nouveau document : un modèle, ou un favori (opportunités seulement).
const TEMPLATE_SOURCE = 'template';
const FAVORITE_SOURCE = 'favorite';
// Icônes du kit Font Awesome (public/index.html) : file-brackets-curly n'existe
// pas dans les paquets SVG installés.
const FAVORITE_STAR_STYLE = {
  '--fa-primary-color': 'rgb(250, 173, 20)',
  '--fa-secondary-color': 'rgb(250, 173, 20)',
  '--fa-secondary-opacity': 0.43,
};
const SOURCE_TABS = [
  {
    key: TEMPLATE_SOURCE,
    label: 'Modèle',
    icon: <i className='fa-duotone fa-file-brackets-curly' aria-hidden='true' />,
  },
  {
    key: FAVORITE_SOURCE,
    label: 'Favori',
    icon: <i className='fa-duotone fa-regular fa-star' style={FAVORITE_STAR_STYLE} aria-hidden='true' />,
  },
];

const { Search } = Input;

const selector = (state) => ({
  handleProposalList: state.handleProposalList,
  currentDocument: state.currentDocument,
  proposals: state.proposals,
  applyProposal: state.applyProposal,
  setProposalId: state.setProposalId,
  setProposal: state.setProposal,
  applyTemplate: state.applyTemplate,
  user: state.user,
  getTemplate: state.getTemplate,
  expandLinkedSectionsAtCreation: state.expandLinkedSectionsAtCreation,
  startLoading: state.startLoading,
  stopLoading: state.stopLoading,
  fetchTables: state.fetchTables,
  defaultVariablesApi: state.defaultVariablesApi,
  deleteProposalStore: state.deleteProposalStore,
  createProposalStore: state.createProposalStore,
  renameProposalStore: state.renameProposalStore,
  duplicateProposalStore: state.duplicateProposalStore,
  setProposalFavoriteStore: state.setProposalFavoriteStore,
  reloadLinkedSections: state.reloadLinkedSections,
  reloadAllApiVariables: state.reloadAllApiVariables,
});

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export const ProposalList = () => {
  const {
    currentDocument,
    proposals,
    getTemplate,
    expandLinkedSectionsAtCreation,
    startLoading,
    stopLoading,
    handleProposalList,
    applyProposal,
    setProposalId,
    setProposal,
    deleteProposalStore,
    createProposalStore,
    renameProposalStore,
    duplicateProposalStore,
    setProposalFavoriteStore,
    reloadLinkedSections,
    reloadAllApiVariables,
    defaultVariablesApi
  } = useStore(selector);
  const { t } = useTranslation();
  const { modal: modalApi, message: messageApi } = App.useApp();
  const [nameSearch, setNameSearch] = useState('');
  const [tableScrollY, setTableScrollY] = useState(360);
  const tableContainerRef = useRef(null);
  const { data: documentTypes } = useQueryDocumentTypes()
  const canUseWeb = canUseWebEditor();
  const [listKind, setListKind] = useState(DOCUMENT_KIND);
  const isWebsiteList = canUseWeb && listKind === WEBSITE_KIND;
  const [creationKind, setCreationKind] = useState(DOCUMENT_KIND);
  const [newWebTemplateId, setNewWebTemplateId] = useState(null);
  const isOpportunity = currentDocument.type === 'opportunity';
  const [creationSource, setCreationSource] = useState(TEMPLATE_SOURCE);
  const [favorites, setFavorites] = useState(null);
  const [selectedFavoriteId, setSelectedFavoriteId] = useState(null);
  // Favoris autorisés sur l'activité de l'opportunité : sans réponse, ni étoile
  // ni onglet « Favori », plutôt qu'une action que le serveur refuserait.
  const [favoritesEnabled, setFavoritesEnabled] = useState(false);
  const isFavoriteCreation =
    creationKind === DOCUMENT_KIND && creationSource === FAVORITE_SOURCE;
  const documentIdentity = useMemo(
    () => ({ id: currentDocument.id, type: currentDocument.type }),
    [currentDocument.id, currentDocument.type],
  );
  const listedProposals = useMemo(
    () => (proposals || []).filter((proposal) => (
      isWebsiteList ? hasWebsite(proposal) : !isStandaloneWebsite(proposal)
    )),
    [proposals, isWebsiteList],
  );

  useEffect(() => {
    if (!isOpportunity) return;
    fetchFavoritesEnabled(currentDocument.id).then(setFavoritesEnabled);
  }, [isOpportunity, currentDocument.id]);

  // 403 « favoris désactivés » d'un onglet ouvert avant la désactivation : on le
  // dit, on relit l'option, et une création en cours repart sur les modèles.
  const handleFavoritesDisabled = () => {
    messageApi.warning(FAVORITES_DISABLED_MESSAGE);
    setFavoritesEnabled(false);
    fetchFavoritesEnabled(currentDocument.id).then(setFavoritesEnabled);
    setCreationSource(TEMPLATE_SOURCE);
    setFavorites(null);
    setSelectedFavoriteId(null);
  };

  useEffect(() => {
    const updateTableHeight = () => {
      if (!tableContainerRef.current) return;
      const rect = tableContainerRef.current.getBoundingClientRect();
      const bottomSpacing = 64;
      const availableHeight = window.innerHeight - rect.top - bottomSpacing;
      setTableScrollY(Math.max(220, availableHeight));
    };

    updateTableHeight();
    window.addEventListener('resize', updateTableHeight);
    return () => {
      window.removeEventListener('resize', updateTableHeight);
    };
  }, [proposals?.length]);

  const cols = [
    {
      title: 'Nom',
      key: 'name',
      //editable: true,
      align: 'left',
      className: 'nameColumn',
      onHeaderCell: () => ({
        style: {
          backgroundColor: '#0B2239',
          color: 'white',
        },
      }),
      onCell: (record) => {
        return {
          style: {
            wordWrap: 'anywhere',
            wordBreak: 'normal',
          },
          onClick: async () => {
            // L'onglet Site web ouvre l'éditeur web, y compris pour la
            // version web d'un document classique.
            if (isWebsiteList) {
              window.location.href = webEditorPath(record.id, documentIdentity);
              return;
            }
            if (currentDocument.type === 'opportunity') {
              emptyUrl();
              updateUrl({ oid: currentDocument.id, pid: record.id });
              setProposalId(record.id);
              Promise.resolve(
                fetchProposal({ id: record.id, type: currentDocument.type }, defaultVariablesApi)
              ).then((fetchedProposal) => {
                applyProposal(
                  fetchedProposal,
                  currentDocument.type,
                  fetchedProposal.sections
                );
              });
            } else if (currentDocument.type === 'offer') {
              emptyUrl();
              updateUrl({ ofid: currentDocument.id, pid: record.id });
              setProposalId(record.id);
              Promise.resolve(
                fetchProposal({ id: record.id, type: currentDocument.type }, defaultVariablesApi)
              ).then((fetchedProposal) => {
                applyProposal(
                  fetchedProposal,
                  currentDocument.type,
                  fetchedProposal.sections
                );
              });
            }
            handleProposalList(false);
          },
        };
      },
      filteredValue: [nameSearch],
      onFilter: (value, record) => {
        return record.name
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .includes(
            value
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .toLowerCase()
          );
      },
      sorter: (a, b) =>
        a.name?.toLowerCase().localeCompare(b.name?.toLowerCase() || ''),
      sortDirections: ['ascend', 'descend'],
      render: (record) => <div>{record.name}</div>,

    },
  ];
  if (isWebsiteList) cols.push({
    title: 'Usage',
    key: 'usage',
    align: 'center',
    onHeaderCell: () => ({
      style: {
        backgroundColor: '#0B2239',
        color: 'white',
      },
    }),
    render: (record) => <Tag bordered={false} color='cyan'>{kindLabel(websiteKind(record))}</Tag>,
    width: 200,
  });
  else cols.push({
    title: 'Format',
    key: 'format',
    align: 'center',
    sorter: (recordA, recordB) => {
      if (recordA.landscape === recordB.landscape) return 0;
      return recordA.landscape ? 1 : -1;
    },
    sortDirections: ['ascend', 'descend'],
    onHeaderCell: () => ({
      style: {
        backgroundColor: '#0B2239',
        color: 'white',
      },
    }),
    render: (record) => (
      <p style={{ textAlign: 'center', margin: 0 }}>
        {record.landscape ? (
          <Tooltip title='Format paysage'>
            <FileOutlined
              style={{
                transform: 'rotate(-90deg)',
                fontSize: 18,
                margin: 0,
                marginRight: 8,
              }}
            />
          </Tooltip>
        ) : (
          <Tooltip title='Format portrait'>
            <FileOutlined
              style={{
                transform: 'scaleX(-1)',
                fontSize: 18,
                margin: 0,
                marginRight: 8,
              }}
            />
          </Tooltip>
        )}
      </p>
    ),
    width: 100,
  });
  // cols.push({
  //   title: 'Aperçu',
  //   key: 'preview',
  //   align: 'center',
  //   width: 300,
  //   onHeaderCell: () => ({
  //     style: {
  //       backgroundColor: '#0B2239',
  //       color: 'white',
  //     },
  //   }),
  //   render: (record) => (
  //     <p style={{ textAlign: 'center', margin: 0 }}>
  //       {record.preview ? (
  //         <center>
  //           <div style={{ width: '40%' }}>
  //             <ModalImage
  //               small={record.preview}
  //               large={record.preview}
  //               alt='Aperçu'
  //             />
  //           </div>
  //         </center>
  //       ) : (
  //         'Aperçu non disponible'
  //       )}
  //     </p>
  //   ),
  // });
  cols.push({
    title: 'Tags',
    key: 'tags',
    align: 'center',
    width: 200,
    onHeaderCell: () => ({
      style: {
        backgroundColor: '#0B2239',
        color: 'white',
      },
    }),
    render: (record) => (
      <p style={{ textAlign: 'center', margin: 0 }}>
        {documentTypes?.filter((dT) => record.document_type_ids.includes(dT.id))
          .map((dT) => dT.name).join(", ")}
      </p>
    ),
  });
  cols.push({
    title: 'Date de création',
    key: 'created_at',
    dataIndex: 'created_at',
    align: 'center',
    onHeaderCell: () => ({
      style: {
        backgroundColor: '#0B2239',
        color: 'white',
      },
    }),
    sorter: (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    sortDirections: ['ascend', 'descend'],
    render: (value) => <div>{formatDate(value)}</div>,
    width: 120,
  });
  cols.push({
    title: 'Dernière modification',
    key: 'updated_at',
    dataIndex: 'updated_at',
    align: 'center',
    onHeaderCell: () => ({
      style: {
        backgroundColor: '#0B2239',
        color: 'white',
      },
    }),
    sorter: (a, b) =>
      new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime(),
    sortDirections: ['ascend', 'descend'],
    render: (value) => <div>{formatDate(value)}</div>,
    width: 120,
  });
  cols.push({
    title: 'Actions',
    key: 'action',
    align: 'center',
    render: (text, record) => {
      // Supprimer ou dupliquer la version web d'un document agirait sur le
      // document classique entier : ces actions restent dans l'onglet Document.
      const documentActions = !(isWebsiteList && isDocumentWebsite(record));
      return (
      <div
        style={{
          display: 'flex',
          fontSize: '1vw',
          justifyContent: 'space-evenly',
        }}
      >
        {documentActions && (
        <Tooltip title='Supprimer'>
          <Popconfirm
            title={'Supprimer ce document ?'}
            onConfirm={() => {
              deleteProposal(record.id).then(() => {
                deleteProposalStore(record.id);
              });
            }}
            okText={t('yes')}
            cancelText={t('cancel')}
          >
            <DeleteOutlined style={{ color: 'red' }} />
          </Popconfirm>
        </Tooltip>
        )}
        {isOpportunity && !isWebsiteList && favoritesEnabled && (
          <Tooltip title={record.is_favorite ? 'Retirer des favoris' : 'Ajouter aux favoris'} mouseLeaveDelay={0}>
            {record.is_favorite ? (
              <StarFilled
                style={{ color: '#faad14' }}
                onClick={() => toggleFavorite(record)}
              />
            ) : (
              <StarOutlined onClick={() => toggleFavorite(record)} />
            )}
          </Tooltip>
        )}
        <Tooltip title='Renommer'>
          <EditOutlined
            onClick={() => {
              showModal('renameProposal', record);
              setEditNewName(record.name);
            }}
          />
        </Tooltip>
        {documentActions && (
        <Tooltip title='Dupliquer'>
          <div style={{ transform: 'rotate(180deg)' }}>
            <CopyOutlined
              style={{ transform: 'scaleX(-1)' }}
              onClick={() => showModal('duplicateProposal', record)}
            />
          </div>
        </Tooltip>
        )}
      </div>
      );
    },
    onHeaderCell: () => ({
      style: {
        backgroundColor: '#0B2239',
        color: 'white',
      },
    }),
    width: 150,
  });

  // Une requête à la fois par document : sinon une réponse tardive afficherait
  // un état que le serveur a déjà défait.
  const [pendingFavorites, setPendingFavorites] = useState(() => new Set());
  const toggleFavorite = (record) => {
    if (pendingFavorites.has(record.id)) return;
    const favorite = !record.is_favorite;
    setPendingFavorites((ids) => new Set(ids).add(record.id));
    setProposalFavorite(record.id, favorite)
      .then((saved) => {
        if (saved === FAVORITES_DISABLED) handleFavoritesDisabled();
        else if (saved) setProposalFavoriteStore(record.id, favorite);
      })
      .finally(() => {
        setPendingFavorites((ids) => {
          const next = new Set(ids);
          next.delete(record.id);
          return next;
        });
      });
  };

  const columns = cols.map((c) => {
    if (!c.editable) return c;
    return {
      ...c,
      onCell: (record) => ({
        record,
        editable: c.editable,
        dataIndex: c.dataIndex,
        title: c.title,
      }),
      onHeaderCell: () => ({
        style: {
          backgroundColor: '#0B2239',
          color: 'white',
        },
      }),
    };
  });
  const [isOpen, setIsOpen] = useModalOpener(false);
  const [modalSelection, setModalSelection] = useState('');
  const [selectedProposal, setSelectedProposal] = useState([]);

  const showModal = (origin, proposal) => {
    setIsOpen(true);
    setModalSelection(origin);
    setSelectedProposal(proposal)
    // Chaque création repart d'un état vierge : sinon le nom, le modèle choisi
    // et le nom auto-rempli d'une ouverture précédente (annulée) survivaient,
    // et un nom tapé égal à cet ancien nom auto-rempli était écrasé au choix
    // d'un modèle.
    if (origin === 'createProposal') {
      setProposalName('');
      autoFilledNameRef.current = '';
      setNewProposalTemplateId(undefined);
      setNewWebTemplateId(null);
      setCreationKind(DOCUMENT_KIND);
      setCreationSource(TEMPLATE_SOURCE);
      setFavorites(null);
      setSelectedFavoriteId(null);
    }
    // setIsModalOpen({
    //   isOpen: true,
    //   modalSelection: origin,
    //   selectedProposal,
    // });
  };

  const [editNewName, setEditNewName] = useState('');
  const [duplicateNewName, setDuplicateNewName] = useState('');
  const [proposalName, setProposalName] = useState('');
  const [newProposalTemplateId, setNewProposalTemplateId] = useState();
  // Lecture des réponses par défaut de l'activité avant la question : « OK » attend
  // en chargement, un second clic ouvrirait une seconde question. La modale
  // ignore « Annuler » pendant ce temps (antd), borné par le délai de la requête.
  const [loadingReloadDefaults, setLoadingReloadDefaults] = useState(false);

  const handleOk = (e) => {
    if (modalSelection === 'renameProposal') {
      renameProposal(selectedProposal.id, editNewName).then((e) => {
        renameProposalStore(e);
      });
    } else if (modalSelection === 'createProposal' && creationKind === WEBSITE_KIND) {
      startLoading();
      createStandaloneWebsite({
        documentIdentity,
        name: proposalName.name,
        templateId: newWebTemplateId,
      })
        .then((created) => {
          if (!created) return;
          createProposalStore(created);
          window.location.href = webEditorPath(created.id, documentIdentity);
        })
        .finally(() => {
          stopLoading();
        });
    } else if (modalSelection === 'createProposal' && isFavoriteCreation) {
      // Le serveur copie le favori ; les rechargements choisis s'appliquent
      // ensuite sur le document ouvert, comme depuis ses boutons. La question
      // est posée avant la copie, par-dessus la création qui reste ouverte :
      // « Retour » y ramène, favori et nom conservés, sans document créé.
      if (loadingReloadDefaults) return;
      setLoadingReloadDefaults(true);
      fetchFavoriteReloadDefaults(currentDocument.id).then((defaults) => {
        setLoadingReloadDefaults(false);
        return askFavoriteReloads(modalApi, defaults);
      }).then((reloads) => {
        if (!reloads) return;
        setIsOpen(false);
        startLoading();
        createProposal(
          currentDocument.id,
          { name: proposalName.name, source_proposal_id: selectedFavoriteId },
          currentDocument.type
        )
          .then(async (created) => {
            if (created === FAVORITES_DISABLED) {
              // Rouvre la création sur les modèles, le nom tapé conservé.
              handleFavoritesDisabled();
              setIsOpen(true);
              return;
            }
            if (!created) return;
            createProposalStore(created);
            await setProposal(created.id, { dedupeSectionStyles: true });
            handleProposalList(false);
            if (reloads.sections) {
              await runReloadLinkedSections({
                reloadLinkedSections,
                modalApi,
                keepAnswers: reloads.keepAnswers,
              });
            }
            // Enregistré par l'autosave, comme depuis le bouton du formulaire.
            if (reloads.variables) await reloadAllApiVariables();
          })
          .finally(() => {
            stopLoading();
          });
      });
      return;
    } else if (modalSelection === 'createProposal') {
      let proposalContent = {};
      // Loader plein écran pendant la création : l'expansion des sections liées
      // fetch les templates + tables admin (comme un chargement de document).
      startLoading();
      Promise.resolve(getTemplate(newProposalTemplateId))
        .then(async (fetchedTemplate) => {
          // Expanse les sections liées AVANT le POST : le proposal est créé avec
          // son contenu déjà expansé (plus de SECTION_LINK brut en DB), donc plus
          // de ré-injection/divergence entre co-éditeurs au load. Format
          // editorState (sortie de getTemplate) → textToRaw seulement ensuite.
          // Doublons de styles du modèle (injections d'avant le dédoublonnage) :
          // le document naît sans eux, même si le modèle n'a pas été rouvert.
          const expanded = dedupeSectionStyles(await expandLinkedSectionsAtCreation({
            content: fetchedTemplate.content,
            templateId: newProposalTemplateId,
            documentType: currentDocument.type,
            documentId: currentDocument.id,
            landscape: fetchedTemplate.landscape,
          }));
          proposalContent.content = {
            ...expanded,
            boxes: mutateDefaultBoxValue(textToRaw(expanded.boxes)),
            triggers: triggersToRaw(expanded.triggers, expanded.boxes),
          };
          proposalContent.landscape = fetchedTemplate.landscape;
          proposalContent.work = null;
          proposalContent.name = proposalName.name;
          proposalContent.document_type_ids = fetchedTemplate.document_type_ids;
          proposalContent.origin = TEMPLATE_SOURCE;
          const created = await createProposal(
            currentDocument.id,
            proposalContent,
            currentDocument.type
          );
          createProposalStore(created);
          if (created) {
            // Entrer directement dans le document créé plutôt que de revenir
            // sur la liste : même enchaînement que le clic sur une ligne
            // existante (setProposalId + fetch + applyProposal), factorisé
            // dans setProposal.
            await setProposal(created.id);
            handleProposalList(false);
          }
        })
        .finally(() => {
          stopLoading();
        });
    } else if (modalSelection === 'duplicateProposal') {
      startLoading();
      duplicateProposal(selectedProposal.id, duplicateNewName, selectedProposal.name)
        .then(async (duplicated) => {
          if (!duplicated) return;
          duplicateProposalStore(duplicated, selectedProposal.document_type_ids);
          // Ouvre la copie, comme à la création : même destination qu'un clic
          // sur sa ligne (un site web dupliqué en reste un, cf. clone_proposal).
          if (isWebsiteList) {
            window.location.href = webEditorPath(duplicated.id, documentIdentity);
            return;
          }
          // La copie reprend les doublons de styles de l'original : nettoyée
          // à l'ouverture, comme un document créé depuis un modèle.
          await setProposal(duplicated.id, { dedupeSectionStyles: true });
          handleProposalList(false);
        })
        .finally(() => {
          stopLoading();
        });
    }
    setIsOpen(false)
    // setIsModalOpen({ ...isModalOpen, isOpen: false });
  };

  const handleCancel = () => {
    setIsOpen(false);
    // setIsModalOpen({ ...isModalOpen, isOpen: false });
    setEditNewName('');
    setDuplicateNewName('');
  };

  // Dernier nom posé automatiquement depuis un modèle : tant que le champ le
  // contient encore, l'utilisateur ne l'a pas modifié et on peut le remplacer
  // au clic sur un autre modèle. Un nom tapé à la main n'est jamais écrasé.
  const autoFilledNameRef = useRef('');

  const handleTemplateSelection = (templateName) => {
    if (!templateName) return;
    if (!proposalName.name || proposalName.name === autoFilledNameRef.current) {
      autoFilledNameRef.current = templateName;
      setProposalName({
        ...proposalName,
        name: templateName,
      });
    }
  };

  const [isCreateButtonHovered, setIsCreateButtonHovered] = useState(false);
  const createButton = (
    <Button
      ghost
      size='large'
      icon={<PlusCircleOutlined />}
      onClick={() => showModal('createProposal')}
      onMouseEnter={() => {
        setIsCreateButtonHovered(true);
      }}
      onMouseLeave={() => {
        setIsCreateButtonHovered(false);
      }}
      style={{
        backgroundColor: isCreateButtonHovered ? '#2166ab' : 'white',
        color: isCreateButtonHovered ? 'white' : '#2166ab',
        marginBottom: '3%',
        borderColor: '#2166ab',
        fontSize: '0.9vw',
        // fontWeight: 'bold',
        borderRadius: '30px',
        border: '1.5px solid ',
      }}
    >
      {canUseWeb ? 'Nouveau' : 'Créer un nouveau document'}
    </Button>
  );
  return (
    <div name='mainDiv' style={{ width: '70%', margin: '0 auto' }}>
      <div style={{ fontSize: '3vw', textAlign: 'center', marginBottom: '5%' }}>
        Liste des documents
      </div>
      {proposals?.length !== 0 ? (
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {createButton}
          </div>
          <div
            style={{
              display: 'flex',
              width: '70%',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '1%',
            }}
          >
            <div style={{ width: '40%' }}>Recherche par nom:</div>
            <Search
              key='nameSearch'
              placeholder='Rechercher un document par nom'
              value={nameSearch}
              style={{
                width: '60%',
                marginBottom: '2%',
                marginTop: '2%',
                marginLeft: '2%',
              }}
              onChange={(e) => {
                setNameSearch(e.target.value);
              }}
            />
          </div>
          {canUseWeb && <Tabs activeKey={listKind} onChange={setListKind} items={KIND_TABS} centered />}
          <div ref={tableContainerRef}>
            <Table
              className='proposal-list-table'
              pagination={false}
              scroll={{ y: tableScrollY }}
              columns={columns}
              dataSource={listedProposals}
              bordered
              showSorterTooltip={false}
            />
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5vw' }}>Aucun document disponible</div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {createButton}
          </div>
        </div>
      )}
      <Modal
        className={modalSelection === 'createProposal' ? 'template-modal-fixed-height' : undefined}
        title={
          modalSelection === 'renameProposal'
            ? 'Edition du document'
            : modalSelection === 'createProposal'
              ? (canUseWeb ? 'Nouveau document ou site web' : 'Création du document')
              : modalSelection === 'duplicateProposal'
                ? 'Duplication du document'
                : null
        }
        open={isOpen}
        onOk={(e) => {
          // Sans modèle, la création appelait getTemplate(undefined) : 404,
          // modale refermée et aucun document créé, sans rien dire.
          const chosenSourceId = creationKind === WEBSITE_KIND
            ? newWebTemplateId
            : isFavoriteCreation ? selectedFavoriteId : newProposalTemplateId;
          if (modalSelection === 'createProposal' && !chosenSourceId) {
            alert(isFavoriteCreation
              ? 'Choisissez un favori pour créer le document'
              : 'Choisissez un modèle pour créer le document');
            return;
          }
          // Le nom de la modale ouverte seulement : proposalName passe de ''
          // à { name } au premier caractère tapé, et un test global sur les
          // trois champs laissait passer un nom effacé.
          const nameByModal = {
            renameProposal: editNewName,
            createProposal: proposalName.name,
            duplicateProposal: duplicateNewName,
          };
          if (nameByModal[modalSelection]?.trim()) {
            handleOk(e);
          } else {
            alert('Le nom du document ne peut pas être vide');
          }
        }}
        onCancel={handleCancel}
        confirmLoading={loadingReloadDefaults}
        // Démonte le contenu à la fermeture : la sélection de modèle de l'arbre
        // (état interne de DocumentsFolderView) repart elle aussi de zéro.
        destroyOnHidden
        width={modalSelection === 'createProposal' ? '60%' : '50%'}
        cancelText={t('cancel')}
      >
        {modalSelection === 'renameProposal' ? (
          <div name='RenameProposalComponent-MainDiv'>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div style={{ width: '30%' }}>Nouveau nom:</div>
              <Input
                key='renameInput'
                placeholder='Nouveau nom'
                value={editNewName}
                size='middle'
                onChange={(e) => {
                  setEditNewName(e.target.value);
                }}
              />
            </div>
          </div>
        ) : modalSelection === 'createProposal' ? (
          // CREATE PROPOSAL MODAL
          <div
            name='proposalNameComponent-MainDiv'
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div style={{ width: '30%' }}>
                {creationKind === WEBSITE_KIND
                  ? 'Nom du nouveau site web:'
                  : 'Nom du nouveau document:'}
              </div>
              <Input
                key='createNameInput'
                placeholder={
                  creationKind === WEBSITE_KIND
                    ? 'Nom du nouveau site web'
                    : 'Nom du nouveau document'
                }
                value={proposalName.name}
                size='middle'
                onChange={(e) => {
                  setProposalName({
                    ...proposalName,
                    name: e.target.value,
                  });
                }}
              />
            </div>
            <div
              style={{ textAlign: 'center', fontSize: '1vw', marginTop: '3%' }}
            >
              {isFavoriteCreation ? 'Choisissez un favori' : 'Choisissez un modèle'}
            </div>

            {/* flex:1/minHeight:0 : DocumentsFolderView mesure la hauteur
                disponible via ResizeObserver sur ce conteneur (cf.
                setTreeWrapNode) pour dimensionner son arbre virtualisé. Sans
                ça, ce div en flow normal ne prend que la hauteur de son
                contenu et l'arbre reste minuscule au milieu de la modale. */}
            {canUseWeb && (
              <Tabs
                activeKey={creationKind}
                onChange={setCreationKind}
                items={KIND_TABS}
                centered
              />
            )}
            {isOpportunity && favoritesEnabled && creationKind === DOCUMENT_KIND && (
              <Tabs
                activeKey={creationSource}
                onChange={(source) => {
                  setCreationSource(source);
                  if (source === FAVORITE_SOURCE && favorites === null)
                    fetchFavoriteProposals(currentDocument.id).then((result) => {
                      if (result === FAVORITES_DISABLED) handleFavoritesDisabled();
                      else setFavorites(result);
                    });
                }}
                items={SOURCE_TABS}
                centered
                size='large'
                tabBarStyle={{ fontWeight: 500 }}
              />
            )}
            <div style={{ flex: 1, minHeight: 0, marginTop: 12 }}>
              {isFavoriteCreation ? (
                <FavoritePicker
                  favorites={favorites}
                  selectedId={selectedFavoriteId}
                  onSelect={(favorite) => {
                    setSelectedFavoriteId(favorite.id);
                    // Le libellé personnel, celui sous lequel l'utilisateur l'a choisi.
                    handleTemplateSelection(favorite.label || favorite.name);
                  }}
                />
              ) : creationKind === WEBSITE_KIND ? (
                <WebTemplatesPanel
                  fetchTemplates={() => fetchStandaloneTemplates(documentIdentity)}
                  onApply={(id, template) => {
                    setNewWebTemplateId(id);
                    handleTemplateSelection(template.name);
                  }}
                />
              ) : (
                <DocumentsFolderView
                  templatesOnly
                  onApply={(id, item) => {
                    setNewProposalTemplateId(id);
                    handleTemplateSelection(item?.name);
                  }}
                />
              )}
            </div>
          </div>
        ) : modalSelection === 'duplicateProposal' ? (
          <div name='DuplicateProposalComponent-MainDiv'>
            <div style={{ marginBottom: '3%', display: 'flex' }}>
              Duplication du document:
              <div style={{ fontWeight: 'bold', marginLeft: '1%' }}>
                {selectedProposal.name}
              </div>
            </div>
            <div>Nom du document dupliqué:</div>
            <Input
              key='duplicateInput'
              placeholder='Nouveau nom du document'
              value={duplicateNewName}
              size='middle'
              onChange={(e) => {
                setDuplicateNewName(e.target.value);
              }}
            />
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

// Fond de la ligne choisie : celui dérivé de la couleur primaire du thème,
// bleu nuit, rendait le texte peu lisible.
const FAVORITE_PICKER_THEME = {
  components: { Table: { rowSelectedBg: '#e8eef5', rowSelectedHoverBg: '#dce5ef' } },
};

const FavoritePicker = ({ favorites, selectedId, onSelect }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
    <ConfigProvider theme={FAVORITE_PICKER_THEME}>
      <Table
        size='small'
        rowKey='id'
        loading={favorites === null}
        dataSource={favorites ?? []}
        pagination={false}
        scroll={{ y: 260 }}
        rowSelection={{
          type: 'radio',
          selectedRowKeys: selectedId ? [selectedId] : [],
          onChange: (_, [favorite]) => onSelect(favorite),
        }}
        onRow={(favorite) => ({ onClick: () => onSelect(favorite) })}
        locale={{
          emptyText:
            "Aucun favori pour cette activité. Ajoutez-en avec l'étoile de la liste des documents.",
        }}
        columns={[
          // Libellé personnel du favori (page « Favoris Editor ») : il suffit à
          // le reconnaître, sans l'opportunité d'origine, qui se lisait comme
          // celle où créer le document.
          { title: 'Favori', dataIndex: 'label', key: 'label' },
          {
            title: 'Dernière modification',
            dataIndex: 'updated_at',
            key: 'updated_at',
            width: 170,
            render: (value) => formatDate(value),
          },
        ]}
      />
    </ConfigProvider>
  </div>
);

// Question posée à chaque création depuis un favori, dans une seule fenêtre.
// Résout { variables, sections, keepAnswers }, ou null sur « Retour ».
const RELOAD_QUESTIONS = [
  {
    key: 'variables',
    label: 'Recharger les variables ?',
    yes: "Les variables prennent les données de cette opportunité.",
    no: "Le document garde les données de l'opportunité d'origine.",
  },
  {
    key: 'sections',
    label: 'Recharger les sections liées ?',
    yes: 'Les sections liées reprennent leur version actuelle de la bibliothèque ; les modifications faites dans ces sections du favori sont perdues.',
    no: 'Les sections liées restent telles que dans le favori.',
  },
];

const FavoriteReloadQuestions = ({ choices }) => {
  const idPrefix = useId();
  const [answers, setAnswers] = useState({ ...choices });
  const answer = (key, value) => {
    // eslint-disable-next-line no-param-reassign
    choices[key] = value;
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
      <span style={CONSEQUENCE_STYLE}>
        Le document est une copie du favori. Choisissez ce qui est mis à jour avec cette opportunité.
      </span>
      {RELOAD_QUESTIONS.map(({ key, label, yes, no }) => (
        <div key={key} style={QUESTION_BLOCK_STYLE}>
          <span id={`${idPrefix}-${key}`} style={QUESTION_STYLE}>{label}</span>
          <Radio.Group
            aria-labelledby={`${idPrefix}-${key}`}
            value={answers[key]}
            onChange={(e) => answer(key, e.target.value)}
            options={[answerOption('Oui', true), answerOption('Non', false)]}
          />
          <span style={CONSEQUENCE_STYLE}>{answers[key] ? yes : no}</span>
          {key === 'sections' && answers.sections && (
            <div style={NESTED_QUESTION_STYLE}>
              <LinkedSectionAnswersChoice choices={choices} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Sous-question des sections : rattachée à la question qui la fait apparaître.
const NESTED_QUESTION_STYLE = {
  marginTop: 8,
  paddingLeft: 12,
  borderLeft: '3px solid #d5dde7',
};

const askFavoriteReloads = async (modalApi, defaults) => {
  const choices = { ...defaults };
  const confirmed = await modalApi.confirm({
    title: (
      <span style={RELOAD_TITLE_STYLE}>
        <i className='fa-duotone fa-arrows-rotate' aria-hidden='true' style={{ marginRight: 10 }} />
        Recharger les données ?
      </span>
    ),
    icon: null,
    content: <FavoriteReloadQuestions choices={choices} />,
    okText: 'Créer le document',
    cancelText: 'Retour',
    centered: true,
    width: 560,
  });
  return confirmed ? { ...choices } : null;
};
